import React, { useEffect, useMemo, useState } from "react";
import { ReactComponent as MarkAllReadIcon } from "../../assets/icons/mark-all-read.svg";
import {
  useNotifications,
  useUnreadNotificationsCount,
} from "../../app/queries/notifications";
import Loader from "../../components/common/Loader";
import EmptyNotifications from "../../components/NotificationsCentre/EmptyNotifications";
import NotificationCard from "../../components/NotificationsCentre/NotificationCard";
import NotificationFilters from "../../components/NotificationsCentre/NotificationFilters";
import NotificationReceiptModal from "../../components/NotificationsCentre/NotificationReceiptModal";
import {
  groupNotificationsByDate,
  mapApiNotifications,
} from "../../components/NotificationsCentre/mapNotification";
import {
  DateGroupLabel,
  ToastBanner,
} from "../../components/NotificationsCentre/styles";
import {
  NotificationFilter,
  NotificationItem,
  NotificationTypeFilter,
} from "../../components/NotificationsCentre/types";
import { useNotificationActions } from "../../components/NotificationsCentre/useNotificationActions";
import {
  Disclaimer,
  FiltersBar,
  List,
  LoadMoreButton,
  LoadMoreWrap,
  MarkAllButton,
  PageHeader,
  PageTitle,
  TypeFilterPill,
  TypeFilters,
  Wrapper,
} from "./styles";

const PAGE_SIZE = 6;

const TYPE_FILTERS: { key: NotificationTypeFilter; label: string }[] = [
  { key: "all", label: "All types" },
  { key: "payment_links", label: "Payment links" },
  { key: "settlements", label: "Settlements" },
  { key: "payouts", label: "Payouts" },
  { key: "collections", label: "Collections" },
  { key: "wire", label: "Wire" },
];

const matchesTypeFilter = (
  item: NotificationItem,
  typeFilter: NotificationTypeFilter
) => {
  switch (typeFilter) {
    case "all":
      return true;
    case "payment_links":
      return item.category === "Payment Links";
    case "settlements":
      return item.category === "Settlements";
    case "payouts":
      return item.category === "Payouts";
    case "collections":
      return item.category === "Collections";
    case "wire":
      return item.category === "Wire";
    default: {
      const _exhaustive: never = typeFilter;
      return _exhaustive;
    }
  }
};

const getEmptyStateContent = (
  filter: NotificationFilter,
  typeFilter: NotificationTypeFilter,
  hasLoadedNotifications: boolean,
  hasFilteredResults: boolean
) => {
  const isCategoryFilterActive = typeFilter !== "all";

  if (isCategoryFilterActive && hasLoadedNotifications && !hasFilteredResults) {
    return {
      title: "No notifications here yet",
      subtitle: "Try another category or switch back to All types.",
    };
  }

  if (filter === "unread") {
    return {
      title: "You are all caught up",
      subtitle: "Every transaction and payout update has been read.",
    };
  }

  return {
    title: "No notifications yet",
    subtitle: "Updates about your transactions and payment links will appear here.",
  };
};

const Notifications = () => {
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const [typeFilter, setTypeFilter] = useState<NotificationTypeFilter>("all");
  const [page, setPage] = useState(0);
  const [accumulatedNotifications, setAccumulatedNotifications] = useState<
    NotificationItem[]
  >([]);

  const onlyUnread = filter === "unread";
  const isCategoryFilterActive = typeFilter !== "all";

  const { data: unreadCountData } = useUnreadNotificationsCount();
  const { data: allNotificationsMeta } = useNotifications({
    page: 0,
    size: 1,
    onlyUnread: false,
  });
  const { data, isLoading, isFetching } = useNotifications({
    page,
    size: PAGE_SIZE,
    onlyUnread,
  });

  const {
    showMarkedToast,
    isMarkingAllAsRead,
    handleMarkAllAsRead,
    handleNotificationClick,
    handleViewTransaction,
    handleDownloadReceipt,
    receiptOpen,
    receipt,
    receiptError,
    receiptLoading,
    closeReceipt,
  } = useNotificationActions();

  const unreadCount = unreadCountData ?? 0;
  const allCount = allNotificationsMeta?.totalElements ?? 0;

  useEffect(() => {
    if (!data) {
      return;
    }

    const responsePage = data.number;
    // Ignore keepPreviousData from a previous page while the next page loads.
    if (responsePage !== page) {
      return;
    }

    const mapped = mapApiNotifications(data.content ?? []);

    setAccumulatedNotifications((prev) => {
      if (responsePage === 0) {
        return mapped;
      }

      const existingIds = new Set(prev.map((item) => item.id));
      const nextItems = mapped.filter((item) => !existingIds.has(item.id));
      return [...prev, ...nextItems];
    });
  }, [data, page]);

  const handleFilterChange = (next: NotificationFilter) => {
    setFilter(next);
    setPage(0);
    setAccumulatedNotifications([]);
  };

  const filteredNotifications = useMemo(() => {
    return accumulatedNotifications.filter((item) =>
      matchesTypeFilter(item, typeFilter)
    );
  }, [accumulatedNotifications, typeFilter]);

  const groupedNotifications = useMemo(
    () => groupNotificationsByDate(filteredNotifications),
    [filteredNotifications]
  );

  const hasLoadedNotifications = accumulatedNotifications.length > 0;
  const hasFilteredResults = filteredNotifications.length > 0;

  const remainingCount = Math.max(
    (data?.totalElements ?? 0) - accumulatedNotifications.length,
    0
  );

  // Type filters are client-side only, so under a category filter we can keep
  // fetching pages until the server says we're on the last page — but we must
  // not show a remaining count based on unfiltered totals.
  const showLoadMore = !isLoading && !data?.last;
  const loadMoreLabel = isFetching
    ? "Loading..."
    : isCategoryFilterActive
      ? "Load more"
      : `Load ${remainingCount} more`;

  const emptyStateContent = getEmptyStateContent(
    filter,
    typeFilter,
    hasLoadedNotifications,
    hasFilteredResults
  );

  const handleLoadMore = () => {
    if (!data?.last && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <Wrapper>
      <PageHeader>
        <PageTitle>Notifications</PageTitle>
        <MarkAllButton
          type="button"
          onClick={() => handleMarkAllAsRead(unreadCount)}
          disabled={unreadCount === 0 || isMarkingAllAsRead}
        >
          <MarkAllReadIcon />
          Mark all as read
        </MarkAllButton>
      </PageHeader>

      <FiltersBar>
        <NotificationFilters
          activeFilter={filter}
          allCount={allCount}
          unreadCount={unreadCount}
          onChange={handleFilterChange}
        />
        <TypeFilters>
          {TYPE_FILTERS.map((item) => (
            <TypeFilterPill
              key={item.key}
              type="button"
              $active={typeFilter === item.key}
              onClick={() => setTypeFilter(item.key)}
            >
              {item.label}
            </TypeFilterPill>
          ))}
        </TypeFilters>
      </FiltersBar>

      <List>
        {isLoading && page === 0 ? (
          <Loader isLoading isComponentType />
        ) : filteredNotifications.length === 0 ? (
          <EmptyNotifications
            title={emptyStateContent.title}
            subtitle={emptyStateContent.subtitle}
          />
        ) : (
          groupedNotifications.map((group) => (
            <React.Fragment key={group.label}>
              <DateGroupLabel>{group.label}</DateGroupLabel>
              {group.items.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  layout="page"
                  onClick={handleNotificationClick}
                  onViewTransaction={handleViewTransaction}
                  onDownloadReceipt={handleDownloadReceipt}
                />
              ))}
            </React.Fragment>
          ))
        )}
      </List>

      {showLoadMore && (isCategoryFilterActive || remainingCount > 0) && (
        <LoadMoreWrap>
          <LoadMoreButton
            type="button"
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            {loadMoreLabel}
          </LoadMoreButton>
        </LoadMoreWrap>
      )}

      <Disclaimer>
        Notifications older than 30 days are cleared. Full history stays in
        transactions page.
      </Disclaimer>

      <NotificationReceiptModal
        open={receiptOpen}
        loading={receiptLoading}
        error={receiptError}
        receipt={receipt}
        onClose={closeReceipt}
      />

      {showMarkedToast && (
        <ToastBanner role="status">
          All notifications marked as read
        </ToastBanner>
      )}
    </Wrapper>
  );
};

export default Notifications;
